import { useState, useEffect } from 'react';
import { HiOutlineArrowDownTray, HiOutlineXMark, HiOutlineCheckCircle } from 'react-icons/hi2';
import { MdDownload } from 'react-icons/md';
import { SiSalesforce } from 'react-icons/si';
import CustomDropdown from '../CustomDropdown/CustomDropdown';
import MultiSelectDropdown from '../MultiSelectDropdown/MultiSelectDropdown';
import toast from 'react-hot-toast';
import { useAssigneeSearch } from '../../hooks/useAssigneeSearch';
import './ExportOpportunitiesDialog.css';

export interface CrmFormData {
  topic: string;
  currentSituation: string;
  customerNeed: string;
  customerPainPoints: string;
  estCloseDate: string;
  estRevenue: number;
  probability: string;
  pricePerStore: number;
  opportunitySiteType: string;
  proposedSolutions: string[];
  assignee: string;
}

interface ExportOpportunitiesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  filteredStoresCount: number;
  totalStoresCount: number;
  selectedRowsCount?: number;
  recommendedCount?: number;
  onExport: (scope: 'selected' | 'all') => void;
  onExportCrm?: (scope: 'selected' | 'all', crmData: CrmFormData) => Promise<{ recordUrl: string }>;
  brandName?: string;
  onNavigate?: (path: string) => void;
  selectedStores?: any[];
  initialStep?: 1 | 2;
}

export default function ExportOpportunitiesDialog({
  isOpen,
  onClose,
  filteredStoresCount,
  totalStoresCount,
  selectedRowsCount = 0,
  recommendedCount = 0,
  onExport,
  onExportCrm,
  brandName,
  onNavigate,
  selectedStores = [],
  initialStep = 1
}: ExportOpportunitiesDialogProps) {
  const [exportType, setExportType] = useState<'xlsx' | 'crm'>('xlsx');
  const [step, setStep] = useState<1 | 2 | 'success'>(1);
  const [crmRecordUrl, setCrmRecordUrl] = useState('');

  // CRM form fields
  const [topic, setTopic] = useState('');
  const [currentSituation, setCurrentSituation] = useState('');
  const [customerNeed, setCustomerNeed] = useState('');
  const [customerPainPoints, setCustomerPainPoints] = useState('');
  const [estCloseDate, setEstCloseDate] = useState('');
  const [estRevenue, setEstRevenue] = useState('');
  const [probability, setProbability] = useState('');

  const [proposedSolutions, setProposedSolutions] = useState<string[]>([]);
  const [assignee, setAssignee] = useState('');
  const [crmExporting, setCrmExporting] = useState(false);
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const { results: assigneeResults, loading: assigneeLoading, handleSearch } = useAssigneeSearch();

  useEffect(() => {
    if (isOpen) {
      setStep(initialStep);
      if (initialStep === 2) setExportType('crm');
      setCrmRecordUrl('');
    }
  }, [isOpen, initialStep]);

  // Derive proposed solutions from selected stores' recommendations
  useEffect(() => {
    if (selectedStores.length > 0) {
      const solutionsSet = new Set<string>();
      selectedStores.forEach((store: any) => {
        if (store.recommendations) {
          if (store.recommendations.purchek?.length > 0) solutionsSet.add('Purchek');
          if (store.recommendations.containment?.length > 0) solutionsSet.add('Containment');
          if (store.recommendations.cartManager?.length > 0) solutionsSet.add('Cart Manager');
          if (store.recommendations.subscription?.length > 0) solutionsSet.add('Subscription');
          if (store.recommendations.pairedWheel?.length > 0) solutionsSet.add('Paired Wheel');
          if (store.recommendations.wheelReorder?.length > 0) solutionsSet.add('Wheel Reorder');
          if (store.recommendations.pendingService?.length > 0) solutionsSet.add('Pending Service');
        }
      });
      setProposedSolutions(Array.from(solutionsSet));
    }
  }, [selectedStores]);

  const handleNext = () => {
    if (step === 1 && exportType === 'xlsx') {
      handleExport();
    } else if (step === 1 && exportType === 'crm') {
      setStep(2);
    }
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
    }
  };

  const handleCancel = () => {
    setStep(1);
    setExportType('xlsx');
    setCrmExporting(false);
    setCrmRecordUrl('');
    onClose();
  };

  const handleExport = () => {
    onExport('selected');
    handleCancel();
  };

  const handleCrmExport = async () => {
    if (!onExportCrm) return;
    if (!topic.trim()) {
      toast.error('Topic is required');
      return;
    }
    setCrmExporting(true);
    try {
      const crmData: CrmFormData = {
        topic,
        currentSituation,
        customerNeed,
        customerPainPoints,
        estCloseDate,
        estRevenue: parseFloat(estRevenue) || 0,
        probability,
        pricePerStore: selectedRowsCount > 0 ? Math.round((parseFloat(estRevenue) || 0) / selectedRowsCount) : 0,
        opportunitySiteType: 'Group',
        proposedSolutions,
        assignee,
      };
      const result = await onExportCrm('selected', crmData);
      setCrmRecordUrl(result.recordUrl);
      setCrmExporting(false);
      setStep('success');
    } catch {
      setCrmExporting(false);
    }
  };

  if (!isOpen) return null;

  const totalSteps = exportType === 'crm' ? 2 : 1;

  return (
    <div className="export-dialog-overlay">
      <div className="export-dialog-container">
        <div className="export-dialog-header">
          <div className="export-dialog-title">
            <HiOutlineArrowDownTray className="export-icon" />
            Export Opportunities
          </div>
          <button
            className="export-dialog-close"
            onClick={handleCancel}
            title="Close"
          >
            <HiOutlineXMark />
          </button>
        </div>

        <div className="export-dialog-content">
          {step !== 'success' && (
            <div className="export-step-indicator">
              Step {step} of {totalSteps}
              <div className="export-step-stats">
                <span className="export-selected-count">{selectedRowsCount} store{selectedRowsCount !== 1 ? 's' : ''} selected</span>
                {recommendedCount > 0 && <span className="export-recommended-count">{recommendedCount} opportunities</span>}
              </div>
            </div>
          )}

          {step === 1 ? (
            <>
              <h3 className="export-section-title">Select Export Type</h3>
              <div className="export-options">
                <label className="export-option">
                  <input
                    type="radio"
                    name="export-type"
                    value="xlsx"
                    checked={exportType === 'xlsx'}
                    onChange={(e) => setExportType(e.target.value as 'xlsx' | 'crm')}
                    className="export-radio"
                  />
                  <div className="export-option-content">
                    <div className="export-option-title">
                      <MdDownload className="export-type-icon" />
                      Export as .xlsx
                    </div>
                  </div>
                </label>

                <label className="export-option">
                  <input
                    type="radio"
                    name="export-type"
                    value="crm"
                    checked={exportType === 'crm'}
                    onChange={(e) => setExportType(e.target.value as 'xlsx' | 'crm')}
                    className="export-radio"
                  />
                  <div className="export-option-content">
                    <div className="export-option-title">
                      <SiSalesforce className="export-type-icon" />
                      Export to CRM
                    </div>
                  </div>
                </label>
              </div>
            </>
          ) : step === 2 ? (
            <>
              <div className="crm-beta-banner">
                This is in a beta version. If you have any feedback please contact{' '}
                <a href="mailto:jpande@gatekeepersystems.com">jpande@gatekeepersystems.com</a>
              </div>
              <div className="export-crm-form">
                <div className="crm-form-section">
                  <div className="crm-section-header">
                    <span className="crm-section-title">Opportunity Details</span>
                  </div>
                  <div className="crm-form-fields">
                    <div className="crm-field">
                      <label className="crm-field-label">Topic <span className="crm-required">*</span></label>
                      <input
                        type="text"
                        placeholder="e.g. Group rollout opportunity for loss prevention systems"
                        className="crm-field-input"
                        value={topic}
                        onChange={(e) => setTopic(e.target.value)}
                      />
                    </div>

                    <div className="crm-field">
                      <label className="crm-field-label">Current Situation</label>
                      <textarea
                        placeholder="Describe the customer's current situation"
                        className="crm-field-input crm-field-textarea"
                        rows={2}
                        value={currentSituation}
                        onChange={(e) => setCurrentSituation(e.target.value)}
                      />
                    </div>

                    <div className="crm-field">
                      <label className="crm-field-label">Customer Need</label>
                      <input
                        type="text"
                        placeholder="What does the customer need?"
                        className="crm-field-input"
                        value={customerNeed}
                        onChange={(e) => setCustomerNeed(e.target.value)}
                      />
                    </div>

                    <div className="crm-field">
                      <label className="crm-field-label">Customer Pain Points</label>
                      <input
                        type="text"
                        placeholder="Key pain points"
                        className="crm-field-input"
                        value={customerPainPoints}
                        onChange={(e) => setCustomerPainPoints(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="crm-form-section">
                  <div className="crm-section-header">
                    <span className="crm-section-title">Financial & Timeline</span>
                  </div>
                  <div className="crm-form-fields">
                    <div className="crm-field">
                      <label className="crm-field-label">Estimated Revenue ($)</label>
                      <input
                        type="number"
                        placeholder="600000"
                        className="crm-field-input"
                        value={estRevenue}
                        onChange={(e) => setEstRevenue(e.target.value)}
                      />
                    </div>

                    <div className="crm-field">
                      <label className="crm-field-label">Estimated Close Date</label>
                      <input
                        type="date"
                        className="crm-field-input"
                        value={estCloseDate}
                        onChange={(e) => setEstCloseDate(e.target.value)}
                      />
                    </div>

                    <div className="crm-field">
                      <label className="crm-field-label">Probability (%)</label>
                      <CustomDropdown
                        value={probability}
                        options={[
                          { label: '25% - Prospecting', value: '25' },
                          { label: '50% - Highly Likely Need', value: '50' },
                          { label: '75% - Validated Interest', value: '75' },
                          { label: '100% - Almost Certain Sale', value: '100' },
                        ]}
                        onChange={setProbability}
                        className="crm-dropdown"
                        placeholder="Select probability"
                      />
                    </div>
                  </div>
                </div>

                <div className="crm-form-section">
                  <div className="crm-section-header">
                    <span className="crm-section-title">Configuration</span>
                  </div>
                  <div className="crm-form-fields">
                    <div className="crm-field">
                      <label className="crm-field-label">Proposed Solutions</label>
                      <MultiSelectDropdown
                        values={proposedSolutions}
                        options={['Purchek', 'Containment', 'Cart Manager', 'Subscription', 'Paired Wheel', 'Wheel Reorder', 'Pending Service']}
                        onChange={setProposedSolutions}
                        placeholder="Select solutions"
                        className="crm-dropdown"
                      />
                    </div>

                    <div className="crm-field">
                      <label className="crm-field-label">Assignee (Email)</label>
                      <div className="crm-assignee-search">
                        <input
                          type="email"
                          placeholder="Search assignee by email or name..."
                          className="crm-field-input crm-assignee-input"
                          value={assignee}
                          onChange={(e) => {
                            setAssignee(e.target.value);
                            handleSearch(e.target.value);
                            setShowAssigneeDropdown(true);
                          }}
                          onFocus={() => setShowAssigneeDropdown(true)}
                        />
                        {(assigneeLoading || (showAssigneeDropdown && assigneeResults.length > 0)) && (
                          <div className="crm-assignee-dropdown">
                            {assigneeLoading && (
                              <div className="crm-assignee-item crm-assignee-loading">Searching...</div>
                            )}
                            {!assigneeLoading && assigneeResults.map((user) => (
                              <div
                                key={user.Id}
                                className="crm-assignee-item"
                                onClick={() => {
                                  setAssignee(user.Email);
                                  setShowAssigneeDropdown(false);
                                }}
                              >
                                <div className="crm-assignee-name">{user.Name}</div>
                                <div className="crm-assignee-email">{user.Email}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : step === 'success' ? (
            <div className="crm-success-screen">
              <HiOutlineCheckCircle className="crm-success-icon" />
              <h3 className="crm-success-title">Exported Successfully</h3>
              <p className="crm-success-message">Your opportunity has been created in CRM.</p>
              <div className="crm-success-buttons">
                {crmRecordUrl && (
                  <a
                    href={crmRecordUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="crm-success-link dialog-button"
                  >
                    <SiSalesforce />
                    Show in CRM
                  </a>
                )}
                <button
                  className="crm-success-link dialog-button"
                  onClick={() => {
                    if (onNavigate) {
                      onNavigate('/dashboard/exported-opportunities');
                      handleCancel();
                    }
                  }}
                >
                  All Exported Opportunities
                </button>
              </div>
            </div>
          ) : null}
        </div>

        {step !== 'success' && (
          <div className="export-dialog-footer">
            <button
              className="export-btn-cancel dialog-button"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <div className="export-dialog-footer-right">
              {step === 2 && (
                <button
                  className="export-btn-back dialog-button"
                  onClick={handleBack}
                >
                  Back
                </button>
              )}
              <button
                className={`${step === 2 ? 'export-btn-crm' : (exportType === 'xlsx' ? 'export-btn-download' : 'export-btn-next')} dialog-button`}
                onClick={step === 2 ? handleCrmExport : handleNext}
                disabled={crmExporting}
              >
                {step === 2
                  ? (crmExporting ? 'Exporting...' : 'Export to CRM')
                  : (exportType === 'xlsx' ? 'Download XLSX' : 'Next')}
              </button>
            </div>
          </div>
        )}

        {step === 'success' && (
          <div className="export-dialog-footer">
            <div />
            <button
              className="export-btn-download dialog-button"
              onClick={handleCancel}
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
